import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { Save } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { FormSection, FormActions, useAlert, Spin, Input, Button, Select } from '../ui';

export interface CreneauData {
    Id?: number;
    CodeFiliale?: string;
    CodeS?: string;
    DateHeure?: string;
    DureeMinutes?: number;
    CodeClient?: string | null;
}

/** Convertit une date API ("YYYY-MM-DDTHH:mm:ss" ou ISO) en valeur datetime-local ("YYYY-MM-DDTHH:mm"). */
function toDateTimeLocal(value?: string): string {
    if (!value) return '';
    const s = String(value).replace(' ', 'T');
    return s.length >= 16 ? s.slice(0, 16) : s;
}

/** Convertit une valeur datetime-local en format attendu par l'API ("YYYY-MM-DDTHH:mm:ss"). */
function toApiDateTime(value: string): string {
    if (!value) return value;
    return value.length === 16 ? `${value}:00` : value;
}

const FormCreneau = (props: {
    creneau?: CreneauData | null;
    filiales: any[];
    services: any[];
    onSaved: () => void;
}) => {

    // USER TOKEN
    const authHeader = useAuthHeader()
    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const { register, handleSubmit, setValue, reset } = useForm()
    const [loading, setLoading] = useState<boolean>(false)

    const [filialeVal, setFilialeVal] = useState('');
    const [serviceVal, setServiceVal] = useState('');

    const isEdit = !!props.creneau?.Id

    // Pré-remplissage (UPDATE) ou valeurs par défaut (CREATE)
    useEffect(() => {
        if (props.creneau) {
            setFilialeVal(props.creneau.CodeFiliale || '')
            setServiceVal(props.creneau.CodeS || '')
            setValue('DateHeure', toDateTimeLocal(props.creneau.DateHeure))
            setValue('DureeMinutes', props.creneau.DureeMinutes ?? 30)
        } else {
            reset()
            setFilialeVal('')
            setServiceVal('')
            setValue('DureeMinutes', 30)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.creneau])

    // Les services sont filtrés par la filiale choisie quand l'information est disponible
    const servicesFiltres = props.services.filter((s: any) =>
        !filialeVal || s.CodeFiliale == null || s.CodeFiliale === filialeVal
    );

    const onSubmit = async (data: any) => {
        if (!filialeVal || !serviceVal) {
            alert.warning("Veuillez sélectionner une filiale et un service.")
            return
        }

        const payload: any = {
            Option: isEdit ? 'UPDATE' : 'CREATE',
            CodeFiliale: filialeVal,
            CodeS: serviceVal,
            DateHeure: toApiDateTime(data.DateHeure),
            DureeMinutes: Number(data.DureeMinutes) || 30,
            CodeClient: isEdit ? (props.creneau?.CodeClient ?? null) : null,
            CodeAbonne: user.CodeAbonne,
            CreatedBy: user.CodeU,
        }
        if (isEdit) payload.Id = props.creneau?.Id

        setLoading(true)
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Creneaux.ManageCreneau,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    alert.success(res.Message)
                    reset()
                    props.onSaved()
                } else {
                    alert.warning(res.Message)
                }
            })
            .catch((error) => {
                setLoading(false)
                alert.error(error.message)
            })
    }

    return (
        <>
            <Spin spinning={loading}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormSection title="Rattachement" subtitle="Filiale et service concernés par le créneau">
                        <Select
                            label="Filiale"
                            value={filialeVal}
                            onChange={setFilialeVal}
                            options={props.filiales.map((f: any) => ({ value: f.CodeFiliale, label: f.CodeFiliale + ' - ' + f.Libelle }))}
                        />
                        <Select
                            label="Service"
                            value={serviceVal}
                            onChange={setServiceVal}
                            options={servicesFiltres.map((s: any) => ({ value: s.CodeS, label: s.CodeS + ' - ' + s.Libelle }))}
                        />
                    </FormSection>

                    <FormSection title="Horaire" subtitle="Date, heure de début et durée du rendez-vous">
                        <Input
                            {...register("DateHeure")}
                            required
                            type='datetime-local'
                            label={'Date et heure'}
                        />
                        <Input
                            {...register("DureeMinutes")}
                            required
                            type='number'
                            min={5}
                            step={5}
                            defaultValue={30}
                            label={'Durée (minutes)'}
                            placeholder={"Ex : 30"}
                            helperText={"Durée par défaut : 30 minutes"}
                        />
                    </FormSection>

                    <FormActions>
                        <Button variant='secondary' type='submit' startIcon={<Save size={16} />}>
                            {isEdit ? 'Mettre à jour' : 'Enregistrer'}
                        </Button>
                    </FormActions>
                </form>
            </Spin>
        </>
    );
};

export default FormCreneau;
